import React, { ReactNode, useCallback } from "react";

import { faKey } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form from "antd/es/form";
import Input from "antd/es/input";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";

import { Alert, Button, Divider, FormItem } from "@mapmycustomers/ui";

import commonStyles from "../../styles/AuthCommon.module.scss";
import AuthLinkComponent from "../../types/AuthLinkComponent";

import styles from "./LoginForm.module.scss";
import messages from "./messages";

const SSO_INQUIRY_LINK = "https://share.hsforms.com/1aoERY4lrQ0GOQeRlXahNAQ3is1h";

export interface LoginFormValues {
  password: string;
  username: string;
}

export interface LoginFormProps {
  className?: string;
  errorMessage?: ReactNode;
  forgotPasswordUrl: string;
  isLoading: boolean;
  LinkComponent: AuthLinkComponent;
  onForgotPasswordClick?: () => void;
  onResetError?: () => void;
  onSsoLoginClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onSubmit: (values: LoginFormValues) => void;
  signUpUrl: string;
  ssoLoginUrl: string;
  withRegistrationLink?: boolean;
  withRestorePasswordLink?: boolean;
}

const LoginForm = ({
  className,
  errorMessage,
  forgotPasswordUrl,
  isLoading,
  LinkComponent,
  onForgotPasswordClick,
  onResetError,
  onSsoLoginClick,
  onSubmit,
  signUpUrl,
  ssoLoginUrl,
  withRegistrationLink = false,
  withRestorePasswordLink = true,
}: LoginFormProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const handleOnForgotPassword = useCallback(() => {
    onResetError?.();
    onForgotPasswordClick?.();
  }, [onForgotPasswordClick, onResetError]);

  return (
    <section className={cn(commonStyles.container, className)}>
      <div className={styles.loginDiv}>
        <div className={styles.loginPage}>
          <div>
            {errorMessage && (
              <Alert className={styles.errorMessage} message={errorMessage} type="error" />
            )}
            <Form form={form} id="login" layout="vertical" name="login" onFinish={onSubmit}>
              {withRegistrationLink && (
                <p className={commonStyles.description}>
                  <FormattedMessage
                    id="auth.login.form.signUp"
                    defaultMessage="Not yet registered? <link>Sign up</link>"
                    description="Sign up link for Login page"
                    values={{
                      link: (text: ReactNode) => (
                        <LinkComponent to={signUpUrl}>{text}</LinkComponent>
                      ),
                    }}
                  />
                </p>
              )}
              <FormItem
                colon={false}
                label={intl.formatMessage(messages.email)}
                name="username"
                required
                requiredMark="optional"
                rules={[
                  {
                    message: intl.formatMessage(messages.emailRequired),
                    required: true,
                  },
                ]}
              >
                <Input autoComplete="username" name="username" size="large" type="email" />
              </FormItem>
              <FormItem
                className={styles.passwordField}
                colon={false}
                label={intl.formatMessage(messages.password)}
                name="password"
                required
                requiredMark="optional"
                rules={[
                  {
                    message: intl.formatMessage(messages.passwordRequired),
                    required: true,
                  },
                ]}
              >
                <Input.Password
                  autoComplete="current-password"
                  name="current-password"
                  size="large"
                  spellCheck="false"
                />
              </FormItem>
              {withRestorePasswordLink && (
                <LinkComponent
                  className={styles.forgotPasswordLink}
                  onClick={handleOnForgotPassword}
                  to={forgotPasswordUrl}
                >
                  {intl.formatMessage(messages.forgotPasswordLink)}
                </LinkComponent>
              )}
              <FormItem>
                <Button
                  block
                  className={styles.loginButton}
                  htmlType="submit"
                  loading={isLoading}
                  type="primary"
                >
                  {isLoading
                    ? intl.formatMessage(messages.loading)
                    : intl.formatMessage(messages.submit)}
                </Button>
              </FormItem>
              <Divider plain>{intl.formatMessage(messages.divider)}</Divider>
              <div className={styles.container}>
                <LinkComponent to={ssoLoginUrl}>
                  <Button
                    block
                    leftIcon={<FontAwesomeIcon icon={faKey} />}
                    onClick={onSsoLoginClick}
                    type="filled"
                  >
                    {intl.formatMessage(messages.ssoLogin)}
                  </Button>
                </LinkComponent>
                <Button
                  className={styles.inquire}
                  href={SSO_INQUIRY_LINK}
                  target="_blank"
                  type="link"
                >
                  {intl.formatMessage(messages.inquire)}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginForm;
